import React, { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import isBetween from "dayjs/plugin/isBetween"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import { useQuery } from "@apollo/client"
import SEARCH_DISCOUNT_CODE_RULES from "../../../graphql/queries/discount/SearchDiscountCodeRules"
import SEARCH_DISCOUNT_RULES from "../../../graphql/queries/discount/SearchDiscountRules"
import SEARCH_EXTERNAL_RULES from "../../../graphql/queries/discount/SearchDiscountExternalRules"
import { handleErrorMessages } from "helpers/errors"
import CheckboxSimple from "../../Ui/Form/CheckboxSimple"
import {
  DiscountCodeRuleSearchHit,
  DiscountRuleSearchHit,
  DiscountExternalRuleSearchHit
} from "lib/types/generated/graphql-types"
import {
  ButtonContainer,
  ButtonWrapper,
  ChartContent,
  ChartContentInner,
  ChartHeader,
  ChartRow,
  ChartWrapper,
  ColorInfo,
  Container,
  ExpandIcon,
  FilterContainer,
  FilterRow,
  Header,
  HeaderCell,
  MonthButton,
  MonthCell,
  ShowMore,
  SkeletonBar,
  SkeletonGroup,
  SkeletonGroupHeader,
  TimelineTrack,
  TypeHeader
} from "./DiscountRuleTimeLine.styled"
import {
  StyledTooltip,
  TooltipAction,
  TooltipContent,
  TooltipRow,
  TooltipTitle,
  TooltipType
} from "./DiscountRuleBar.styled"
import { ReactComponent as ChevronDown } from "images/icons/chevron-down.svg"
import { ReactComponent as ChartIcon } from "images/icons/chart-simple-horizontal.svg"
import { ReactComponent as ArrowRightFromLine } from "images/icons/arrow-right-from-line.svg"
import { ReactComponent as ArrowRightToLine } from "images/icons/arrow-right-to-line.svg"
import { theme } from "../../../theme"
import DiscountRuleBar, { DiscountRuleData } from "./DiscountRuleBar"

dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

interface TransformedRule {
  name: string
  id: string
  validFrom: string
  validTo: string
  color: string
  ruleType: "code" | "cart" | "external"
  isStackable: boolean
  isExclusive: boolean
  applyLast: boolean
  sortOrder: number
  groupKey: string
  isPreview?: boolean
  isLongTerm?: boolean
}

type DiscountRule = DiscountCodeRuleSearchHit | DiscountRuleSearchHit | DiscountExternalRuleSearchHit

interface DiscountRuleTimeLineProps {
  previewRule?: {
    name: string
    validFrom: string
    validTo: string
    ruleType: "code" | "cart" | "external"
    sortOrder: number
    isStackable: boolean
    isExclusive?: boolean
    applyLast?: boolean
  }
}

const DiscountRuleTimeLine = ({ previewRule }: DiscountRuleTimeLineProps = {}) => {
  const [codeRules, setCodeRules] = useState<DiscountCodeRuleSearchHit[]>([])
  const [cartRules, setCartRules] = useState<DiscountRuleSearchHit[]>([])
  const [externalRules, setExternalRules] = useState<DiscountExternalRuleSearchHit[]>([])
  const [isChartExpanded, setIsChartExpanded] = useState(true)
  const [monthsToShow, setMonthsToShow] = useState(3)
  const [hoveredRule, setHoveredRule] = useState<DiscountRuleData | null>(null)
  const [initialExpanded, setInitialExpanded] = useState(false)
  const [showCodeRules, setShowCodeRules] = useState(true)
  const [showCartRules, setShowCartRules] = useState(true)
  const [showExternalRules, setShowExternalRules] = useState(true)
  const [showExpiredRules, setShowExpiredRules] = useState(false)
  const [showUpcomingRules, setShowUpcomingRules] = useState(true)
  const [showLongTermRules, setShowLongTermRules] = useState(true)

  const generateMonths = () => {
    const startDate = dayjs().subtract(1, "month").startOf("month")
    return Array.from({ length: monthsToShow }, (_, index) => {
      const monthStart = startDate.add(index, "month").startOf("month")
      const monthEnd = monthStart.endOf("month")
      return {
        name: monthStart.format(monthsToShow > 6 ? "MMM YY" : "MMM YYYY"),
        start: monthStart.toISOString(),
        end: monthEnd.toISOString()
      }
    })
  }

  const queryVariables = useMemo(() => {
    const startOfPreviousMonth = dayjs().subtract(1, "month").startOf("month").toISOString()

    return {
      from: 0,
      size: 100,
      sort: [{ field: "sortOrder", order: "ASC" }],
      query: JSON.stringify({
        bool: {
          must: [
            // Only active rules
            {
              term: {
                isActive: true
              }
            }
          ],
          should: [
            // Rules that haven't ended yet (ended after the start of the previous month)
            {
              range: {
                validTo: {
                  gte: startOfPreviousMonth
                }
              }
            },
            // Rules with no validTo (always active)
            {
              bool: {
                must_not: {
                  exists: {
                    field: "validTo"
                  }
                }
              }
            }
          ],
          minimum_should_match: 1
        }
      })
    }
  }, [])

  const { loading: codeRulesLoading } = useQuery(SEARCH_DISCOUNT_CODE_RULES, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setCodeRules(data?.searchDiscountCodeRules?.hits || [])
    },
    onError: (err) => {
      handleErrorMessages(err)
    }
  })

  const { loading: cartRulesLoading } = useQuery(SEARCH_DISCOUNT_RULES, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setCartRules(data?.searchDiscountRules?.hits || [])
    },
    onError: (err) => {
      handleErrorMessages(err)
    }
  })

  const { loading: externalRulesLoading } = useQuery(SEARCH_EXTERNAL_RULES, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setExternalRules(data?.searchDiscountExternalRules?.hits || [])
    },
    onError: (err) => {
      handleErrorMessages(err)
    }
  })

  const months = generateMonths()
  const loading = codeRulesLoading || cartRulesLoading || externalRulesLoading

  const filterAndTransformRules = () => {
    const now = dayjs()
    const allRules: TransformedRule[] = []

    const getGroupKey = (rule: DiscountRule, ruleType: "code" | "cart" | "external") => {
      const ruleWithProps = rule as DiscountRule & { isExclusive?: boolean; applyLast?: boolean }

      if (ruleWithProps.applyLast) {
        if (ruleWithProps.isExclusive) {
          return `apply-last-exclusive-${ruleType}`
        }
        return `apply-last-${ruleType}`
      }

      if (ruleWithProps.isExclusive) {
        return `exclusive-${ruleType}`
      }
      if (rule.isStackable) {
        return `stackable-${ruleType}`
      }
      return `non-stackable-${ruleType}`
    }

    const processRules = (
      rules: DiscountRule[],
      ruleType: "code" | "cart" | "external",
      activeColor: string,
      upcomingColor: string
    ) => {
      rules.forEach((rule) => {
        const defaultStartDate = "2025-01-01T00:00:00.000Z"
        const start = dayjs(rule.validFrom ?? defaultStartDate)

        // For display purposes, if no validTo, use a far future date
        const end = rule.validTo ? dayjs(rule.validTo) : dayjs().add(100, "years")
        const isLongTerm = !rule.validTo

        // Determine color based on date ranges
        let color = activeColor

        // If the rule has expired (ended before now), use gray
        if (end.isBefore(now)) {
          color = theme.colors.grey
        }
        // If the rule hasn't started yet, use upcoming color
        else if (start.isAfter(now)) {
          color = upcomingColor
        }

        const ruleWithProps = rule as DiscountRule & { isExclusive?: boolean; applyLast?: boolean }
        allRules.push({
          id: rule.id,
          name: rule.name,
          validFrom: rule.validFrom,
          validTo: rule.validTo || end.toISOString(),
          color,
          ruleType,
          isStackable: rule.isStackable || false,
          isExclusive: ruleWithProps.isExclusive || false,
          applyLast: ruleWithProps.applyLast || false,
          sortOrder: rule.sortOrder || 0,
          groupKey: getGroupKey(rule, ruleType),
          isLongTerm
        })
      })
    }

    processRules(codeRules, "code", theme.colors.blue, theme.colors.yellow)
    processRules(cartRules, "cart", theme.colors.turquoiseDark, theme.colors.yellow)
    processRules(externalRules, "external", theme.colors.orange, theme.colors.yellow)

    return allRules
  }

  const allRules = useMemo(
    () => filterAndTransformRules(),
    [codeRules, cartRules, externalRules]
  )

  // Set initial expansion state when there are few rules (only after data loads)
  useEffect(() => {
    if (!loading && allRules.length > 0 && allRules.length < 8 && !initialExpanded) {
      setInitialExpanded(true)
    }
  }, [loading, allRules.length, initialExpanded])

  // Add preview rule if provided (when creating or updating a rule)
  const rulesWithPreview = previewRule
    ? [
        ...allRules,
        {
          id: "preview",
          name: previewRule.name,
          validFrom: previewRule.validFrom,
          validTo: previewRule.validTo,
          color: theme.colors.greenDark,
          ruleType: previewRule.ruleType,
          isStackable: previewRule.isStackable,
          isExclusive: previewRule.isExclusive || false,
          applyLast: previewRule.applyLast || false,
          sortOrder: previewRule.sortOrder,
          groupKey: "preview",
          isPreview: true
        }
      ]
    : allRules

  // Filter rules based on all checkboxes
  const filteredRules = rulesWithPreview.filter((rule) => {
    // Filter by rule type
    if (!showCodeRules && rule.ruleType === "code") {
      return false
    }
    if (!showCartRules && rule.ruleType === "cart") {
      return false
    }
    if (!showExternalRules && rule.ruleType === "external") {
      return false
    }
    // Filter expired rules if showExpiredRules is false
    if (!showExpiredRules && rule.color === theme.colors.grey) {
      return false
    }
    // Filter upcoming rules if showUpcomingRules is false
    if (!showUpcomingRules && rule.color === theme.colors.yellow) {
      return false
    }
    // Filter long-term rules if showLongTermRules is false
    return !(!showLongTermRules && rule.isLongTerm);

  })

  // Group by the groupKey and sort by sortOrder within each group
  const groupedRulesObj = filteredRules.reduce(
    (acc, rule) => {
      const groupKey = rule.groupKey
      if (!acc[groupKey]) {
        acc[groupKey] = []
      }
      acc[groupKey].push(rule)
      return acc
    },
    {} as Record<string, TransformedRule[]>
  )

  // Sort rules within each group by sortOrder, then by validTo date
  Object.keys(groupedRulesObj).forEach((groupKey) => {
    groupedRulesObj[groupKey].sort((a, b) => {
      // First sort by sortOrder
      if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder
      }
      // Then sort by validTo date (earliest end date first)
      return dayjs(a.validTo).valueOf() - dayjs(b.validTo).valueOf()
    })
  })

  // Define group order: preview first, then all cart rules, then all code rules, then all external rules
  const groupOrder = [
    // Preview rules (shown first)
    "preview",
    // All Cart rules
    "exclusive-cart",
    "non-stackable-cart",
    "stackable-cart",
    "apply-last-exclusive-cart",
    "apply-last-cart",
    // All Code rules
    "exclusive-code",
    "non-stackable-code",
    "stackable-code",
    "apply-last-exclusive-code",
    "apply-last-code",
    // All External rules
    "exclusive-external",
    "non-stackable-external",
    "stackable-external",
    "apply-last-exclusive-external",
    "apply-last-external"
  ]

  // Sort grouped rules by the defined order
  const sortedGroupedRules: Record<string, TransformedRule[]> = {}
  groupOrder.forEach((groupKey) => {
    if (groupedRulesObj[groupKey] && groupedRulesObj[groupKey].length > 0) {
      sortedGroupedRules[groupKey] = groupedRulesObj[groupKey]
    }
  })

  const getBarPositionInTimeline = (rule: TransformedRule) => {
    const timelineStart = dayjs(months[0].start)
    const timelineEnd = dayjs(months[months.length - 1].end)
    const ruleStart = dayjs(rule.validFrom ?? timelineStart)
    const ruleEnd = dayjs(rule.validTo)

    const timelineDuration = timelineEnd.diff(timelineStart)

    // Calculate start and end positions relative to timeline
    const ruleStartOffset = ruleStart.diff(timelineStart)
    const ruleEndOffset = ruleEnd.diff(timelineStart)

    let startPercent = (ruleStartOffset / timelineDuration) * 100
    let endPercent = (ruleEndOffset / timelineDuration) * 100

    startPercent = Math.max(0, startPercent)
    endPercent = Math.min(100, endPercent)

    const widthPercent = endPercent - startPercent

    return {
      barStart: `${startPercent}%`,
      barWidth: `${Math.max(0, widthPercent)}%`
    }
  }

  const getGroupDisplayName = (groupKey: string, count: number) => {
    const parts = groupKey.split("-")
    const type = parts[parts.length - 1]
    const typeName = type === "code" ? "Code" : type === "cart" ? "Cart" : "External"
    const isPreview = groupKey.startsWith("preview")
    const counter = ` (${count})`

    if (isPreview) {
      return `Preview Rule`
    }

    if (groupKey.startsWith("apply-last-exclusive")) {
      return `Exclusive Stackable Apply Last ${typeName} Rules${counter}`
    }
    if (groupKey.startsWith("apply-last")) {
      return `Stackable Apply Last ${typeName} Rules${counter}`
    }
    if (groupKey.startsWith("exclusive")) {
      return `Exclusive Stackable ${typeName} Rules${counter}`
    }
    if (groupKey.startsWith("stackable")) {
      return `Stackable ${typeName} Rules${counter}`
    }
    if (groupKey.startsWith("non-stackable")) {
      return `Non-Stackable ${typeName} Rules${counter}`
    }
    return `${groupKey}${counter}`
  }

  const getRuleTypeLabel = (type: string) => {
    switch (type) {
      case "code":
        return "Code Rule"
      case "cart":
        return "Cart Rule"
      case "external":
        return "External Rule"
      default:
        return type
    }
  }

  return (
    <>
      <StyledTooltip
        id="discount-rule-timeline-tooltip"
        delayShow={0}
        delayHide={0}
        render={() =>
          hoveredRule ? (
            <TooltipContent>
              <TooltipType>{getRuleTypeLabel(hoveredRule.ruleType)}</TooltipType>
              <TooltipTitle>{hoveredRule.ruleName}</TooltipTitle>
              <TooltipRow>
                Sort Order: {hoveredRule.sortOrder}
              </TooltipRow>
              <TooltipRow>
                <ArrowRightFromLine />
                {dayjs(hoveredRule.validFrom).format("YYYY-MM-DD, HH:mm")}
              </TooltipRow>
              <TooltipRow>
                <ArrowRightToLine />
                {dayjs(hoveredRule.validTo).format("YYYY-MM-DD, HH:mm")}
              </TooltipRow>
              <TooltipAction>Click to edit</TooltipAction>
            </TooltipContent>
          ) : null
        }
      />
      <Container>
        <ChartHeader onClick={() => setIsChartExpanded(!isChartExpanded)}>
          <span>
            <ChartIcon />
            Discount Rules Timeline
          </span>
          <ExpandIcon $expanded={isChartExpanded}>
            <ChevronDown />
          </ExpandIcon>
        </ChartHeader>
        <ChartContent $expanded={isChartExpanded}>
          <ChartContentInner $expanded={isChartExpanded}>
            <ButtonContainer>
              <FilterContainer>
                <FilterRow>
                  <CheckboxSimple
                    name="showCartRules"
                    label=""
                    value={showCartRules}
                    setValue={setShowCartRules}
                  />
                  <ColorInfo
                    $color={theme.colors.turquoiseDark}
                    $clickable
                    onClick={() => setShowCartRules(!showCartRules)}
                  >
                    Active Cart Rules
                  </ColorInfo>
                </FilterRow>
                <FilterRow>
                  <CheckboxSimple
                    name="showCodeRules"
                    label=""
                    value={showCodeRules}
                    setValue={setShowCodeRules}
                  />
                  <ColorInfo
                    $color={theme.colors.blue}
                    $clickable
                    onClick={() => setShowCodeRules(!showCodeRules)}
                  >
                    Active Code Rules
                  </ColorInfo>
                </FilterRow>
                <FilterRow>
                  <CheckboxSimple
                    name="showExternalRules"
                    label=""
                    value={showExternalRules}
                    setValue={setShowExternalRules}
                  />
                  <ColorInfo
                    $color={theme.colors.orange}
                    $clickable
                    onClick={() => setShowExternalRules(!showExternalRules)}
                  >
                    Active External Rules
                  </ColorInfo>
                </FilterRow>
                <FilterRow>
                  <CheckboxSimple
                    name="showExpiredRules"
                    label=""
                    value={showExpiredRules}
                    setValue={setShowExpiredRules}
                  />
                  <ColorInfo
                    $color={theme.colors.grey}
                    $clickable
                    onClick={() => setShowExpiredRules(!showExpiredRules)}
                  >
                    Expired Rules
                  </ColorInfo>
                </FilterRow>
                <FilterRow>
                  <CheckboxSimple
                    name="showUpcomingRules"
                    label=""
                    value={showUpcomingRules}
                    setValue={setShowUpcomingRules}
                  />
                  <ColorInfo
                    $color={theme.colors.yellow}
                    $clickable
                    onClick={() => setShowUpcomingRules(!showUpcomingRules)}
                  >
                    Upcoming Rules
                  </ColorInfo>
                </FilterRow>
                <FilterRow>
                  <CheckboxSimple
                    name="showLongTermRules"
                    label=""
                    value={showLongTermRules}
                    setValue={setShowLongTermRules}
                  />
                  <ColorInfo
                    $color="#1a1a1a"
                    $clickable
                    onClick={() => setShowLongTermRules(!showLongTermRules)}
                  >
                    Long-Term Rules (black gradient)
                  </ColorInfo>
                </FilterRow>
                {previewRule && (
                  <FilterRow>
                    <ColorInfo $color={theme.colors.greenDark}>Preview Rule</ColorInfo>
                  </FilterRow>
                )}
              </FilterContainer>
              <ButtonWrapper>
                <MonthButton $active={monthsToShow === 3} onClick={() => setMonthsToShow(3)}>
                  3 Months
                </MonthButton>
                <MonthButton $active={monthsToShow === 6} onClick={() => setMonthsToShow(6)}>
                  6 Months
                </MonthButton>
                <MonthButton $active={monthsToShow === 12} onClick={() => setMonthsToShow(12)}>
                  12 Months
                </MonthButton>
              </ButtonWrapper>
            </ButtonContainer>
            <ChartWrapper $initialExpanded={initialExpanded} >
              <Header $monthCount={monthsToShow}>
                {months.map((month) => (
                  <HeaderCell key={month.name}>{month.name}</HeaderCell>
                ))}
              </Header>

              {loading ? (
                <>
                  {[1, 2, 3].map((groupIndex) => (
                    <SkeletonGroup key={groupIndex}>
                      <SkeletonGroupHeader />
                      {[1, 2, 3].map((barIndex) => (
                        <SkeletonBar key={barIndex} width={`${40 + barIndex * 15}%`} />
                      ))}
                    </SkeletonGroup>
                  ))}
                </>
              ) : (
                Object.entries(sortedGroupedRules).map(([groupKey, rules]) => (
                  <div key={groupKey}>
                    <TypeHeader>{getGroupDisplayName(groupKey, rules.length)}</TypeHeader>
                    {rules.map((rule, index) => {
                      const { barStart, barWidth } = getBarPositionInTimeline(rule)
                      return (
                        <ChartRow key={index}>
                          <TimelineTrack $monthCount={monthsToShow}>
                            {months.map((_, monthIndex) => (
                              <MonthCell key={monthIndex} />
                            ))}
                          </TimelineTrack>
                          <DiscountRuleBar
                            color={rule.color}
                            width={barWidth}
                            start={barStart}
                            ruleId={rule.id}
                            ruleName={rule.name}
                            ruleType={rule.ruleType}
                            validFrom={rule.validFrom}
                            validTo={rule.validTo}
                            sortOrder={rule.sortOrder}
                            showName={true}
                            isPreview={rule.isPreview}
                            isLongTerm={rule.isLongTerm}
                            onHoverStart={setHoveredRule}
                            onHoverEnd={() => setHoveredRule(null)}
                          />
                        </ChartRow>
                      )
                    })}
                  </div>
                ))
              )}
            </ChartWrapper>
            <ShowMore onClick={() => setInitialExpanded(!initialExpanded)}>
              {initialExpanded ? "Show less" : "Show more"}
            </ShowMore>
          </ChartContentInner>
        </ChartContent>
      </Container>
    </>
  )
}

export default DiscountRuleTimeLine
